// IdentifyTaskHandler
// -> Manages the Idenitfy Task and builds the Idenitfy

// Import Required Classes
import EventEmitter from "events";

// Import ESRI Classes
import Draw from "@arcgis/core/views/draw/Draw";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";

// Extend the EventEmitter to allow other logic to listen for sidebar events
export default class DrawHandler extends EventEmitter {
  constructor(args = {}) {
    super();

    // Parse the arguments
    this.view = args.view;
    this.render_widget = args.render_widget;
    this.selected = null;
    this.action = null;
    this.layer = args.layer;
    this.clear_graphic = args.clear_graphic || false;
    this.draw_events = [
      "vertex-add",
      "vertex-remove",
      "cursor-update",
      "redo",
      "undo",
      "draw-complete",
    ];

    if (this.clear_graphic) {
      this.draw_events.pop();
    }

    // Set the default polygon symbol
    // TODO Update later to accept custom symbols
    if (args.polygon_symbol) {
      this.polygon_symbol = args.polygon_symbol;
    } else {
      this.polygon_symbol = {
        type: "simple-fill",
        outline: {
          width: 3,
          color: [255, 0, 0, 0.8],
          cap: "round",
          join: "round",
        },
        color: [26, 26, 26, 0.17],
      };
    }

    // Set the default polyline symbol
    if (args.polyline_symbol) {
      this.polyline_symbol = args.polyline_symbol;
    } else {
      this.polyline_symbol = {
        type: "simple-line", // autocasts as new SimpleFillSymbol
        color: [255, 0, 0],
        width: 3,
        cap: "round",
        join: "round",
      };
    }

    if (args.point_symbol) {
      this.point_symbol = args.point_symbol;
    } else {
      this.point_symbol = {
        type: "simple-marker",
        color: [26, 26, 26, 0.5],
        outline: {
          width: 2,
          color: [255, 0, 0, 0.8],
          cap: "round",
          join: "round",
        },
      };
    }

    // Initialize when the view is loaded
    // => Apparently ESRI can't just trip this if the view is loaded... so I have to check the property manually as well
    if (this.view) {
      this.initialize();
    } else {
      this.view.when(() => this.initialize);
    }
  }

  // Starts the draw but doesn't activate it yet
  initialize() {
    this.draw = new Draw({
      view: this.view,
    });
  }

  // Starts up the draw tool and listens for completion
  // => TODO make this it's own ES6 Class
  start(tool) {
    // Set the selected
    this.selected = tool;

    this.action = this.draw.create(this.selected, { mode: "click" });

    // Start to draw based on the geometry
    // => Add callbacks for events since ESRI doesn't have it set up yet (or ever)
    if (this.selected === "polygon") {
      this.action.on(this.draw_events, this.createPolygonGraphic);
    } else if (this.selected === "rectangle") {
      this.action.on(this.draw_events, this.createRectangleGraphic);
    } else if (this.selected === "polyline") {
      this.action.on(this.draw_events, this.createPolylineGraphic);
    } else if (this.selected === "point") {
      this.action.on("draw-complete", this.createPointGraphic);
    }

    // Clear the graphics if set
    if (this.clear_graphic) {
      this.action.on("draw-complete", () => {
        this.layer.graphics.removeAll();
      });
    }

    // Listen for the Draw to complete to get the geometry and query the parcel layer
    this.action.on(["draw-complete"], (event) => {
      if (this.selected === "polygon") {
        this.buildPolygon(event, (geometry) => {
          this.completed(geometry);
        });
      } else if (this.selected === "rectangle") {
        this.buildRectangle(event, (geometry) => {
          this.completed(geometry);
        });
      } else if (this.selected === "polyline") {
        this.buildPolyline(event, (geometry) => {
          this.completed(geometry);
        });
      } else if (this.selected === "point") {
        this.buildPoint(event, (geometry) => {
          this.completed(geometry);
        });
      }
    });
  }

  completed(geometry) {
    // Emit the completed event and pass the event back
    this.emit("completed", geometry);
    // Focus on the view
    this.view.focus();
  }

  // Terminates a Drawing session manually
  end() {
    this.draw.reset();
  }

  buildPolygon = (event, callback) => {
    var polygon = new Polygon({
      rings: event.vertices,
      spatialReference: this.view.spatialReference,
    });
    callback(polygon);
  };

  buildPolyline = (event, callback) => {
    var polyline = new Polyline({
      paths: event.vertices,
      spatialReference: this.view.spatialReference,
    });
    callback(polyline);
  };

  // Handle the Polyline Creation
  createPolylineGraphic = (event) => {
    const vertices = event.vertices;
    this.layer.graphics.removeAll();

    // a graphic representing the polyline that is being drawn
    const graphic = new Graphic({
      geometry: {
        type: "polyline",
        paths: vertices,
        spatialReference: this.view.spatialReference,
      },
      symbol: this.polyline_symbol,
    });

    // check if the polyline intersects itself.
    const intersectingSegment = this.getIntersectingSegment(graphic.geometry);

    // Add a new graphic for the intersecting segment.
    if (intersectingSegment) {
      this.layer.graphics.addMany([graphic, intersectingSegment]);
    }
    // Just add the graphic representing the polyline if no intersection
    else {
      this.layer.graphics.add(graphic);
    }

    // return intersectingSegment
    return {
      selfIntersects: intersectingSegment,
    };
  };

  buildRectangle = (event, callback) => {
    if (event.vertices.length === 2) {
      // Get start and end coordinates
      var [startX, startY] = event.vertices[0];
      var [endX, endY] = event.vertices[1];

      // Create bounding box based on coordinates
      var corners = [
        [startX, startY],
        [endX, startY],
        [endX, endY],
        [startX, endY],
        [startX, startY], // complete the polygon so it's valid
      ];

      // Create a new polygon
      var polygon = new Polygon({
        rings: corners,
        spatialReference: this.view.spatialReference,
      });
      callback(polygon);
    } else {
      return null;
    }
  };

  // Handle Rectangle Creation
  createRectangleGraphic = (event) => {
    //remove existing graphic
    this.layer.graphics.removeAll();

    // Build the Rectangle
    this.buildRectangle(event, (geometry) => {
      if (geometry) {
        // create a new graphic representing the polygon, add it to the view
        var graphic = new Graphic({
          geometry: geometry,
          symbol: this.polygon_symbol,
        });

        this.layer.graphics.add(graphic);
      }
    });
  };

  // Handle Polygon Creation
  createPolygonGraphic = (event) => {
    this.layer.graphics.removeAll();
    this.buildPolygon(event, (geometry) => {
      // Create the graphic
      var graphic = new Graphic({
        geometry: geometry,
        symbol: this.polygon_symbol,
      });
      this.layer.graphics.add(graphic);
    });
  };

  buildPoint = (event, callback) => {
    var point = new Point({
      x: event.vertices[0][0],
      y: event.vertices[0][1],
      spatialReference: this.view.spatialReference,
    });
    callback(point);
  };

  createPointGraphic = (event) => {
    this.layer.graphics.removeAll();
    this.buildPoint(event, (geometry) => {
      // Create the graphic
      var graphic = new Graphic({
        geometry: geometry,
        symbol: this.point_symbol,
      });

      if (!this.clear_graphic) {
        this.layer.graphics.add(graphic);
      }
    });
  };

  // function that checks if the line intersects itself
  isSelfIntersecting = (polyline) => {
    if (polyline.paths[0].length < 3) {
      return false;
    }
    const line = polyline.clone();

    //get the last segment from the polyline that is being drawn
    const lastSegment = this.getLastSegment(polyline);
    line.removePoint(0, line.paths[0].length - 1);

    // returns true if the line intersects itself, false otherwise
    return geometryEngine.crosses(lastSegment, line);
  };

  // Checks if the line intersects itself. If yes, change the last
  // segment's symbol giving a visual feedback to the user.
  getIntersectingSegment = (polyline) => {
    if (this.isSelfIntersecting(polyline)) {
      return new Graphic({
        geometry: this.getLastSegment(polyline),
        symbol: {
          type: "simple-line", // autocasts as new SimpleLineSymbol
          style: "short-dot",
          width: 3.5,
          color: "yellow",
        },
      });
    }
    return null;
  };

  // Get the last segment of the polyline that is being drawn
  getLastSegment = (polyline) => {
    const line = polyline.clone();
    const lastXYPoint = line.removePoint(0, line.paths[0].length - 1);
    const existingLineFinalPoint = line.getPoint(0, line.paths[0].length - 1);

    return {
      type: "polyline",
      spatialReference: this.view.spatialReference,
      hasZ: false,
      paths: [
        [
          [existingLineFinalPoint.x, existingLineFinalPoint.y],
          [lastXYPoint.x, lastXYPoint.y],
        ],
      ],
    };
  };
}
